'use client';

/**
 * Reveal overlay — renders the chalk-dust and pen sprites that animate
 * while a symbol first appears on the tapestry. Two chalk puffs are
 * picked from a small library for variety (seeded by symbol_id so the
 * same symbol always gets the same puffs on re-entry).
 *
 * Why mask-image with luminance instead of mix-blend-mode
 * -------------------------------------------------------
 * The source jpgs are white-chalk-on-black. An earlier version used
 * `mix-blend-mode: screen` to drop the black, but that only blends
 * against the parent stacking context — and the .tapestry-symbol wrapper
 * has `transform` + `will-change`, which creates an isolated stacking
 * context. So screen-blending ended up happening against transparent
 * symbol content, which doesn't actually remove black pixels.
 *
 * Instead, each sprite element is a white-filled div masked by the
 * source jpg's luminance. White areas of the mask stay opaque, black
 * areas become fully transparent. No blend mode, works inside any
 * stacking context.
 *
 * Timing is driven entirely by CSS keyframes in globals.css — this
 * component just mounts the right elements with the right mask urls.
 * Mount and unmount are controlled by the parent, so React cleanup
 * handles cancellation when a scrub unmounts the symbol mid-reveal.
 */

const CHALK_VARIANTS = [
  '/tapestry/chalk-burst.jpg',
  '/tapestry/chalk-wisp.jpg',
  '/tapestry/chalk-scatter-1.jpg',
  '/tapestry/chalk-scatter-2.jpg',
];

// Applied inline (not in CSS) so the renderer's stylesheet carries no url()
// and bundles cleanly across hosts. Resolved at runtime against public root.
const PEN_SRC = '/tapestry/pen.jpg';

/** Deterministic pick of two distinct chalk sprites from a string seed. */
function pickPuffs(seed) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const a = h % CHALK_VARIANTS.length;
  const b = (a + 1 + ((h >> 3) % (CHALK_VARIANTS.length - 1))) % CHALK_VARIANTS.length;
  return [CHALK_VARIANTS[a], CHALK_VARIANTS[b]];
}

function maskStyle(url) {
  return {
    maskImage: `url("${url}")`,
    WebkitMaskImage: `url("${url}")`,
  };
}

export default function TapestryReveal({ seed = '' }) {
  const [puffA, puffB] = pickPuffs(seed);
  return (
    <div className="tapestry-reveal-overlay" aria-hidden="true">
      <div className="tapestry-puff puff-a" style={maskStyle(puffA)} />
      <div className="tapestry-puff puff-b" style={maskStyle(puffB)} />
      <div className="tapestry-pen" style={maskStyle(PEN_SRC)} />
    </div>
  );
}
